/**
 * STYLING: Plain Tailwind ONLY!
 * Dedicated detail page for a single workspace text-template ("Vorlage").
 * - Loads template by id from /v2/tools/text/vorlage/:templateId
 * - System-Prompt is read-only (defined by the template)
 * - User-Prompt + Settings are editable, defaults derived from template
 * - Generate calls /api/v2/text-tools/generate with template_id;
 *   backend defaults tool_code to "text_custom" when none is set.
 */

import { useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle, Button } from '@/components/v2/ui';
import { TextToolInput } from './TextToolInput';
import { TextToolSettings } from './TextToolSettings';
import { VisionImagePicker } from '@/components/v2/shared/VisionImagePicker';
import { BrandPicker } from '@/components/v2/shared/BrandPicker';
import { EID } from '@/lib/element-ids.registry';
import { TextToolResult } from './TextToolResult';
import {
  fetchAllWorkspaceTextTemplates,
  generateText,
  type TextGenerateResponse,
  type TextTemplate,
} from './textGenerationApi';

type S = Record<string, unknown>;
const pick = <T,>(s: S, k: string, fallback: T): T =>
  k in s && s[k] !== null && s[k] !== undefined ? (s[k] as T) : fallback;

export function TextVorlagePage() {
  const { templateId } = useParams<{ templateId: string }>();
  const { t } = useTranslation();
  const { data: templates, isLoading, error: loadError } = useQuery({
    queryKey: ['text-templates', 'all'],
    queryFn: fetchAllWorkspaceTextTemplates,
    staleTime: 60 * 1000,
  });
  const template = useMemo(
    () => templates?.find((tpl) => tpl.id === templateId),
    [templates, templateId],
  );

  if (!templateId) return <Navigate to="/v2/tools/text" replace />;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (loadError || !template) {
    return (
      <div className="container mx-auto p-6 max-w-4xl space-y-4">
        <BackLink />
        <Alert variant="error">
          <AlertTitle>{t('textTools.vorlage.notFoundTitle')}</AlertTitle>
          <AlertDescription>{t('textTools.vorlage.notFoundBody')}</AlertDescription>
        </Alert>
      </div>
    );
  }
  return <TextVorlageForm template={template} />;
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link
      id="text-vorlage-back-link"
      to="/v2/tools/text"
      className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('textTools.allTextTools')}
    </Link>
  );
}

function TextVorlageForm({ template }: { template: TextTemplate }) {
  const { t } = useTranslation();
  const s = (template.settings ?? {}) as S;
  const [prompt, setPrompt] = useState(template.user_prompt ?? '');
  const [model, setModel] = useState(pick<string>(s, 'model', 'gemini-2.5-flash'));
  const [temperature, setTemperature] = useState(pick<number>(s, 'temperature', 0.7));
  const [googleSearch, setGoogleSearch] = useState(pick<boolean>(s, 'google_search', false));
  const [outputFormat, setOutputFormat] = useState(pick<string>(s, 'output_format', 'plain'));
  const [writingStyle, setWritingStyle] = useState(pick<string>(s, 'writing_style', 'balanced'));
  const [naturalStyle, setNaturalStyle] = useState(pick<boolean>(s, 'natural_style', true));
  const [textStyle, setTextStyle] = useState(pick<string>(s, 'text_style', ''));
  const [lengthBucket, setLengthBucket] = useState(pick<string>(s, 'length_bucket', ''));
  const [readingLevel, setReadingLevel] = useState(pick<string>(s, 'reading_level', ''));
  const [goal, setGoal] = useState(pick<string>(s, 'goal', 'standard'));
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(
    pick<string | null>(s, 'brand_id', null),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TextGenerateResponse | null>(null);
  const targetLanguage = pick<string>(s, 'target_language', '');
  const tc = template.tool_code ?? 'text_custom';

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setError(null);
    try {
      const res = await generateText({
        tool_code: tc, template_id: template.id, prompt, model, temperature,
        use_cache: true, json_mode: outputFormat === 'json',
        google_search: googleSearch, output_format: outputFormat,
        writing_style: writingStyle, natural_style: naturalStyle,
        target_language: targetLanguage || undefined,
        text_style: textStyle || undefined, length_bucket: lengthBucket || undefined,
        reading_level: readingLevel || undefined,
        goal: goal !== 'standard' ? goal : undefined,
        image_key: imageKey || undefined,
        brand_id: brandId || undefined,
      });
      setResult(res);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  const id = `vorlage-${template.id}`;
  return (
    <div id={`text-vorlage-page-${template.id}`} className="container mx-auto p-6 max-w-4xl space-y-6">
      <BackLink />
      <header className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 id={`text-vorlage-heading-${template.id}`} className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{template.name}</h1>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            {t('textTools.myTemplates.badge')}
          </span>
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">{t('textTools.vorlage.description')}</p>
      </header>

      <TextToolInput
        toolCode={id} prompt={prompt} onPromptChange={setPrompt}
        systemPrompt={template.system_prompt ?? ''}
        onSystemPromptChange={() => { /* read-only */ }}
        placeholder={template.user_prompt || t('textTools.vorlage.promptPlaceholder')}
        canEditSystem={false}
      />

      <VisionImagePicker imageKey={imageKey} onChange={setImageKey} surchargeHint="+5 CR" />

      <BrandPicker
        id={EID['text-vorlage-brand-dropdown']}
        value={brandId}
        onChange={setBrandId}
      />

      <TextToolSettings
        toolCode={id}
        model={model} onModelChange={setModel}
        temperature={temperature} onTemperatureChange={setTemperature}
        googleSearch={googleSearch} onGoogleSearchChange={setGoogleSearch}
        outputFormat={outputFormat} onOutputFormatChange={setOutputFormat}
        writingStyle={writingStyle} onWritingStyleChange={setWritingStyle}
        naturalStyle={naturalStyle} onNaturalStyleChange={setNaturalStyle}
        textStyle={textStyle} onTextStyleChange={setTextStyle}
        lengthBucket={lengthBucket} onLengthBucketChange={setLengthBucket}
        readingLevel={readingLevel} onReadingLevelChange={setReadingLevel}
        goal={goal} onGoalChange={setGoal}
      />

      <Button
        id={`text-vorlage-generate-btn-${template.id}`}
        onClick={handleGenerate} disabled={loading || !prompt.trim()}
        variant="primary" className="w-full"
        iconLeft={<Sparkles className="h-5 w-5" />}
      >
        {loading ? t('textTools.generating') : t('textTools.generateText')}
      </Button>

      {error && (
        <Alert id={`text-vorlage-error-${template.id}`} variant="error">
          <AlertTitle>{t('textTools.error')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && result.success && <TextToolResult toolCode={id} result={result} />}
    </div>
  );
}
